import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, Upload, Download, X } from "lucide-react";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const ROLE_LABEL = { hd_admin: "Company Admin", hd_agent: "Agent", hd_user: "End User" };
const SAMPLE_CSV = "name,email,role,password\nJane Doe,jane@acme.com,agent,\nJohn Smith,john@acme.com,user,Secret123!\nAlex Admin,alex@acme.com,admin,";

const downloadText = (name, text) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  a.download = name;
  a.click();
};

export default function DeskUsers() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", hd_role: "hd_agent" });
  const [busy, setBusy] = useState(false);
  const [bulk, setBulk] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = () => api.get("/helpdesk/members").then((r) => setMembers(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const uploadCsv = async (file) => {
    if (!file) return;
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/helpdesk/members/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulk({ stage: "done", ...data });
      toast.success(`${data.added.length} member(s) added, ${data.skipped.length} skipped`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBulkBusy(false);
  };

  const downloadResults = () => {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const rows = bulk.added.map((a) => [a.name, a.email, a.role, a.password || "(set in file)"].map(esc).join(","));
    downloadText("new-members.csv", "name,email,role,password\n" + rows.join("\n"));
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/helpdesk/members", form);
      toast.success(`${form.name} added as ${ROLE_LABEL[form.hd_role]}`);
      setShow(false);
      setForm({ name: "", email: "", password: "", hd_role: "hd_agent" });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} (${m.email}) from your workspace?`)) return;
    try { await api.delete(`/helpdesk/members/${m.id}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="desk-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold font-heading">Users</h1>
        <div className="flex flex-wrap gap-2">
          <button data-testid="bulk-upload-button" onClick={() => setBulk({ stage: "pick" })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-300 bg-white hover:bg-neutral-50 text-sm font-medium">
            <Upload size={15} /> Bulk upload
          </button>
          <button data-testid="add-member-button" onClick={() => setShow(true)}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">
            <UserPlus size={15} /> Add member
          </button>
        </div>
      </div>
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
          <thead><tr className="bg-neutral-50">
            {["Name", "Email", "Role", "Joined", ""].map((h, i) => (
              <th key={i} className="px-5 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
          </tr></thead>
          <tbody className="divide-y divide-neutral-100">
            {members.map((m) => (
              <tr key={m.id} data-testid={`member-row-${m.id}`}>
                <td className="px-5 py-3 font-medium">{m.name}</td>
                <td className="px-5 py-3 text-neutral-500">{m.email}</td>
                <td className="px-5 py-3">
                  <span className="text-xs font-medium border border-neutral-200 bg-neutral-50 rounded-full px-2.5 py-0.5">{ROLE_LABEL[m.role] || m.role}</span>
                  {m.role !== "hd_user" && (
                    <span data-testid={`member-avail-${m.id}`} className={`ml-2 inline-flex items-center gap-1 text-xs ${m.hd_available === false ? "text-neutral-400" : "text-emerald-600"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${m.hd_available === false ? "bg-neutral-300" : "bg-emerald-500"}`} />
                      {m.hd_available === false ? "Away" : "Available"}
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-neutral-400">{new Date(m.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3">
                  {m.id !== user?.id && (
                    <button data-testid={`remove-member-${m.id}`} onClick={() => remove(m)}
                      className="p-1.5 text-red-400 hover:text-red-700 hover:bg-red-50 rounded-md"><Trash2 size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {bulk && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="bulk-upload-modal">
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-lg w-full p-7 max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold font-heading text-lg flex items-center gap-2"><Upload size={17} /> Bulk upload members</h3>
              <button data-testid="bulk-close" onClick={() => setBulk(null)} className="p-1.5 hover:bg-neutral-100 rounded-md"><X size={16} /></button>
            </div>
            {bulk.stage === "pick" ? (
              <>
                <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
                  Upload a CSV with columns <code className="bg-neutral-100 px-1 rounded font-mono text-[11px]">name,email,role,password</code>.
                  Role is <b>admin</b>, <b>agent</b> or <b>user</b>. Leave password blank to auto-generate one.
                </p>
                <button data-testid="bulk-sample-download" onClick={() => downloadText("members-sample.csv", SAMPLE_CSV)}
                  className="inline-flex items-center gap-1.5 mt-3 text-xs text-neutral-500 underline hover:text-neutral-800"><Download size={12} /> Download sample CSV</button>
                <label className="mt-5 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-neutral-300 rounded-xl py-10 cursor-pointer hover:bg-neutral-50">
                  <Upload size={22} className="text-neutral-400" />
                  <span className="text-sm font-medium">{bulkBusy ? "Uploading..." : "Choose CSV file"}</span>
                  <input data-testid="bulk-file-input" type="file" accept=".csv,text/csv" className="hidden" disabled={bulkBusy}
                    onChange={(e) => uploadCsv(e.target.files?.[0])} />
                </label>
              </>
            ) : (
              <div className="mt-3" data-testid="bulk-results">
                <p className="text-sm font-medium text-emerald-700" data-testid="bulk-added-count">{bulk.added.length} member(s) added</p>
                {bulk.added.length > 0 && (
                  <>
                    <div className="mt-2 border border-neutral-200 rounded-lg divide-y divide-neutral-100 max-h-48 overflow-y-auto text-xs">
                      {bulk.added.map((a) => (
                        <div key={a.email} className="px-3 py-2 flex items-center justify-between gap-2">
                          <span className="font-medium truncate">{a.name} <span className="text-neutral-400 font-normal">{a.email}</span></span>
                          <span className="font-mono text-neutral-500">{a.password ? a.password : "(set in file)"}</span>
                        </div>
                      ))}
                    </div>
                    <button data-testid="bulk-results-download" onClick={downloadResults}
                      className="inline-flex items-center gap-1.5 mt-2 text-xs text-neutral-500 underline hover:text-neutral-800">
                      <Download size={12} /> Download credentials CSV (share securely)
                    </button>
                  </>
                )}
                {bulk.skipped.length > 0 && (
                  <>
                    <p className="text-sm font-medium text-amber-700 mt-4" data-testid="bulk-skipped-count">{bulk.skipped.length} row(s) skipped</p>
                    <ul className="mt-1 text-xs text-neutral-500 space-y-1 max-h-32 overflow-y-auto">
                      {bulk.skipped.map((s, i) => <li key={i}>Row {s.row} — {s.email}: {s.reason}</li>)}
                    </ul>
                  </>
                )}
                <button data-testid="bulk-done-button" onClick={() => setBulk(null)}
                  className="mt-5 w-full py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Done</button>
              </div>
            )}
          </div>
        </div>
      )}

      {show && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="add-member-modal">
          <form onSubmit={add} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-md w-full p-7">
            <h3 className="font-semibold font-heading text-lg mb-4">Add member</h3>
            <label className="text-sm font-medium text-neutral-700">Name</label>
            <input data-testid="member-name-input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Email</label>
            <input data-testid="member-email-input" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Password (min 8)</label>
            <input data-testid="member-password-input" required minLength={8} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={inputCls} />
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Role</label>
            <select data-testid="member-role-select" value={form.hd_role} onChange={(e) => setForm((f) => ({ ...f, hd_role: e.target.value }))} className={inputCls}>
              <option value="hd_agent">Agent</option>
              <option value="hd_user">End User</option>
              <option value="hd_admin">Company Admin</option>
            </select>
            <div className="flex gap-2 mt-6">
              <button data-testid="member-save-button" disabled={busy}
                className="flex-1 py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">{busy ? "Adding..." : "Add member"}</button>
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-md border border-neutral-300 text-sm font-medium hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
